import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InfoscreenComponent } from '../infoscreen-dumb/infoscreen.component';
import { InfoscreenService } from './infoscreen.service';
import { firstValueFrom } from 'rxjs';
import { InfoScreenData } from '../types/InfoScreenData';
import { Location } from '../types/Location';
import { CalendarPeriod } from '../school-calendar/school-calendar.types';
import { LegacyView, legacyRange, legacyView, localIso, toPeriods } from './legacy-view';

type SpinnerState = 'inactive' | 'loading' | 'done';

@Component({
  selector: 'app-infoscreen-host',
  imports: [InfoscreenComponent],
  template: `
    <app-infoscreen-dumb
      [values]="infoScreenValues"
      [spinnerState]="spinnerState"
      (onProgressCycleEnd)="updateData()"
      [locationName]="location?.name"
      [hasLocationId]="hasLocationId"
      [date]="displayDate"
      [view]="view"
      [periods]="periods"
      [from]="from"
    ></app-infoscreen-dumb>
  `,
})
export class InfoscreenHostComponent implements OnInit {
  infoScreenValues: InfoScreenData[] = [];
  locationId?: number;
  location?: Location;
  spinnerState: SpinnerState = 'inactive';
  hasLocationId: boolean = false;
  displayDate: Date = new Date();
  /** From the address (`?view=list|day|week`): the list, or the school timetable for the day or the week. */
  view: LegacyView = 'list';
  /** The school timetable's bookings, and the first day it shows. */
  periods: CalendarPeriod[] = [];
  from = '';

  constructor(
    private route: ActivatedRoute,
    private infoscreenService: InfoscreenService,
  ) {}

  async ngOnInit() {
    const locationId = this.route.snapshot.queryParams['locationId'];
    this.hasLocationId = !!locationId;
    this.view = legacyView(this.route.snapshot.queryParams['view']);

    this.spinnerState = 'loading';
    if (locationId) {
      const locations: Location[] = await firstValueFrom(
        this.infoscreenService.getLocations(),
      );

      this.location = locations.find(
        (l: Location) => l.id === parseInt(locationId),
      );

      this.locationId = parseInt(locationId);

      await this.updateData();
    }
    this.spinnerState = 'inactive';
  }

  async updateData() {
    this.displayDate = new Date();
    if (this.locationId && this.view !== 'list') {
      // The school timetable: the whole day or week, ended bookings too — they stay in their place.
      // A failed fetch (network, 5xx) keeps the last timetable on the wall and tries again at the
      // next minute; it must not throw out of here, or the start-up spinner would stay on.
      const { from, to } = legacyRange(this.view, this.displayDate);
      await this.delay(500);
      try {
        const values = await firstValueFrom(this.infoscreenService.get(this.locationId, localIso(from), localIso(to)));
        this.from = localIso(from);
        this.periods = toPeriods(values);
      } catch (error) {
        console.warn('Skolekalenderen kunne ikke hentes; den forrige vises, til næste forsøg.', error);
      }
      return;
    }
    if (this.locationId) {
      const values = await this.getData(this.locationId);

      values.sort((a, b) => {
        return a.start.localeCompare(b.start);
      });

      this.infoScreenValues = this.ensureEndedBookingPeriodsAreNotShown(values);
    }
  }

  async getData(locationId: number): Promise<InfoScreenData[]> {
    await this.delay(500);
    return await firstValueFrom(this.infoscreenService.get(locationId));
  }

  delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  ensureEndedBookingPeriodsAreNotShown = (values: InfoScreenData[]) => {
    const now = new Date();
    return values.filter((val: InfoScreenData) => {
      return new Date(val.end) > now;
    });
  };
}
